import { useState, useEffect } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import EditQueryNameModal from './EditQueryNameButton';
import { useUser } from '../../../../contexts/UserContext';
import { PRIVATE_ROUTE, buildPath } from '../../../../routes/routes';
import { formatDateTimeUTC } from '../../../../utils/timeUtils';
import CopyableText from '../../../elements/copyable-text/CopyableText';
import Loading from '../../../elements/loading/Loading';
import Modal from '../../../sections/modal/Modal';
import NoContent from '../../../sections/no-content/NoContent';
import UserAvatar from '../../../sections/user-avatar/UserAvatar';

const LoadQueryButton = ({
  getQueryList,
  onEditQueryName,
  onLoadQuery,
  onDeleteQuery,
}) => {
  const { projectId, instanceId } = useParams();
  const { t } = useTranslation();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [queryList, setQueryList] = useState([]);

  const toggleModal = () => setModal(!modal);

  const fetchQueryList = async () => {
    const queryListRes = await getQueryList();
    setQueryList(queryListRes);
    setLoading(false);
  };

  useEffect(() => {
    if (!modal) return;
    fetchQueryList();
  }, [modal]);

  const handleLoad = (query) => {
    onLoadQuery(query);
    toggleModal();
  };

  const handleDelete = async (queryId) => {
    await onDeleteQuery(queryId);
    fetchQueryList();
  };

  const handleEditQueryName = async (queryId, payload) => {
    await onEditQueryName(queryId, payload);
    fetchQueryList();
  };

  const tableHeaders = [
    t('queryName', { ns: 'instance' }),
    t('collection', { ns: 'instance' }),
    t('creator', { ns: 'instance' }),
    t('createDate', { ns: 'instance' }),
    '',
  ];

  return (
    <>
      <Button
        variant="outlined"
        onClick={toggleModal}
        startIcon={<ViewListIcon />}
      >
        {t('queryList', { ns: 'buttons' })}
      </Button>
      <Modal
        openModal={modal}
        title={t('search.queryList', { ns: 'sectionTitles' })}
        handleClose={toggleModal}
        maxWidth="md"
      >
        {loading ? (
          <Loading />
        ) : queryList.length > 0 ? (
          <TableContainer sx={{ maxHeight: 350, mb: '1em' }}>
            <Table aria-label="load query table" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, i) => (
                    <TableCell key={header} align={i === 0 ? 'left' : 'center'}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {queryList.map((query) => (
                  <TableRow key={query.id}>
                    <TableCell align="left">{query.name}</TableCell>
                    <TableCell align="center">{query.collection}</TableCell>
                    <TableCell align="center">
                      <UserAvatar user={query.author} />
                    </TableCell>
                    <TableCell align="center">
                      {formatDateTimeUTC(query.createdAt, user?.timeZone)}
                    </TableCell>
                    <TableCell align="right">
                      <EditQueryNameModal
                        onEditQueryName={handleEditQueryName}
                        query={query}
                      />
                      <Tooltip
                        title={t('loadQuery', { ns: 'tooltips' })}
                        aria-label="load"
                        placement="bottom"
                      >
                        <IconButton
                          onClick={() => handleLoad(query)}
                          size="large"
                        >
                          <SystemUpdateAltIcon />
                        </IconButton>
                      </Tooltip>
                      <CopyableText
                        iconOnly
                        text={`${window.location.origin}${buildPath({
                          path: PRIVATE_ROUTE.INSTANCE_SEARCH,
                          params: {
                            projectId: projectId,
                            instanceId: instanceId,
                          },
                        })}?query=${query.id}`}
                        tooltip={t('copyQueryLink', { ns: 'tooltips' })}
                      />
                      <Tooltip
                        title={t('deleteQuery', { ns: 'tooltips' })}
                        placement="bottom"
                      >
                        <IconButton
                          onClick={() => handleDelete(query.id)}
                          aria-label="delete"
                          size="large"
                        >
                          <DeleteForeverIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ mb: 2 }}>
            <NoContent title={t('noQuery', { ns: 'instance' })} />
          </Box>
        )}
      </Modal>
    </>
  );
};

LoadQueryButton.propTypes = {
  getQueryList: PropTypes.func.isRequired,
  onEditQueryName: PropTypes.func.isRequired,
  onLoadQuery: PropTypes.func.isRequired,
  onDeleteQuery: PropTypes.func.isRequired,
};

export default LoadQueryButton;
